// Copyright (c) Docugami, Inc. All rights reserved.

import { IDragDropEvents, IIconProps, IListProps } from "@fluentui/react";
import { getFileSizeStringForKb, isDirectory, UnassignedDocSetId } from "components/file_management/file_helpers";
import DocumentListView, { IDocumentPageViewItem, ListViewColumns } from "components/file_management/presentation/DocumentListView";
import { emptyGray } from "components/shell/presentation/AppStyles";
import { BasicIconProps, ColorIconProps } from "components/work_path/IconRenderer";
import dayjs from "dayjs";
import { DgDirectory, FileManagerInstance, FileProperties } from "filemanager/FileManager";
import { FileStatusInfo } from "filemanager/FileStatus";
import { dgIconNames } from "hooks/dgIcons";
import { useDocumentDiagnostics } from "hooks/diagnostics/DocumentDiagnostics";
import { ClusterDocument, IngestionWarnings } from "networking/api";
import { ProviderId } from "networking/StorageProviders";
import { forwardRef, Ref, useEffect, useRef, useState } from "react";
import { getProviderFriendlyName } from "storage/StorageProviderHelpers";
import { DocsetDocument, SortableDocumentField } from "store/frontend/frontendSlice";
import styled from "styled-components";

const ViewerContainer = styled.div`
    width: 100%;
    height: 100%;
`
export interface IDocumentViewItemStatus {
    color: string;
    message: string;
    tooltip?: string;
    iconProps?: IIconProps;
}

export type PropertyCallbacks<T, Return> = {
    [P in keyof T]?: (item: T) => Return;
}

export interface IDocumentViewItem {
    id: string; // The document's filepath
    name: string;
    status?: IDocumentViewItemStatus;
    folderId: string;
    docSet: string;
    createdAt: string;
    size: string;
    icon: BasicIconProps;
    disabledToolTip?: string;
    isDisabled?: boolean;
    isNew?: boolean;
    fileWarnings?: IngestionWarnings;
    docSource?: string;
}

export interface IDocumentViewControls {
    clearSelection(): void
    selectAll(): void
    selectItems(ids: string[], clearSelection?: boolean): void
}

export interface DocumentViewerProps {
    isLoading?: boolean;
    items: IDocumentPageViewItem[];
    selectedItems: string[];
    onSelectedItemsChanged: (items: string[]) => void;
    singleSelect?: boolean;
    filter?: ListViewColumns;
    isTransparent?: boolean;
    links?: PropertyCallbacks<IDocumentViewItem, string>;
    actions?: PropertyCallbacks<IDocumentViewItem, void>
    validateActions?: PropertyCallbacks<IDocumentViewItem, boolean>;
    dragDropEvents?: IDragDropEvents;
    onSort?: (field: string | undefined, descending?: boolean) => void;
    sortableColumns?:(keyof IDocumentViewItem)[];
    listProps?: IListProps;
    disableSelectAll?: boolean;
}

export const DocumentViewer = forwardRef((props: DocumentViewerProps, ref: Ref<IDocumentViewControls>): JSX.Element => {
    const [diagnosticItem, setDiagnosticItem] = useState<FileProperties | null>(null);
    const viewRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let newItem: FileProperties | null = null;
        const filePath = props.selectedItems.find(x => !isDirectory(x));
        if (!!filePath) {
            newItem = FileManagerInstance.getFile(filePath)?.properties ?? null;
        }

        setDiagnosticItem(newItem);
    }, [props.selectedItems])

    const diagnosticsPane = useDocumentDiagnostics(diagnosticItem?.folderId ?? '', viewRef.current!, diagnosticItem?.path);

    return (
        <ViewerContainer ref={viewRef}>
            <DocumentListView
                ref={ref}
                isLoading={props.isLoading}
                items={props.items}
                isTransparent={props.isTransparent ?? false}
                filter={props.filter ?? ListViewColumns.All}
                singleSelection={props.singleSelect ?? false}
                selectedItemsChanged={props.onSelectedItemsChanged}
                links={props.links}
                actions={props.actions}
                validateActions={props.validateActions}
                dragDropEvents={props.dragDropEvents}
                sortableColumns={props.sortableColumns}
                onSort={props.onSort}
                listProps={props.listProps}
                disableSelectAll={props.disableSelectAll} />
            {diagnosticsPane}
        </ViewerContainer>
    );
});

export function documentToViewItem(document: ClusterDocument | DocsetDocument, statusInfo: FileStatusInfo, isNew?: boolean, providerId?: ProviderId): IDocumentViewItem {
    const createdAtUtc = dayjs(document.createdAt);
    return {
        id: document.fileId,
        name: document.filename,
        status: {
            color: statusInfo.color,
            message: statusInfo.message,
            tooltip: statusInfo.details,
            iconProps: statusInfo.iconProps
        },
        folderId: document.folderId,
        docSet: document.clusterId ?? UnassignedDocSetId,
        createdAt: createdAtUtc.add(createdAtUtc.utcOffset(), 'minute').format('MMM D, YYYY'),
        size: getFileSizeStringForKb(document.sizeBytes ? Math.floor(document.sizeBytes / 1024) : 0),
        icon: getIconForFileViewItem(false, providerId),
        isNew: isNew,
        fileWarnings: document.ingestionWarnings,
        docSource: getDocSourceFriendlyName(providerId)
    }
}

export function fileToViewItem(properties: FileProperties, statusInfo: FileStatusInfo, isNew?: boolean, providerId?: ProviderId): IDocumentViewItem {
    const createdAtUtc = dayjs(properties.createdAt);
    return {
        id: properties.path,
        name: properties.name,
        status: {
            color: statusInfo.color,
            message: statusInfo.message,
            tooltip: statusInfo.details,
            iconProps: statusInfo.iconProps
        },
        folderId: properties.folderId ?? '',
        docSet: properties.clusterId ?? '',
        createdAt: createdAtUtc.add(createdAtUtc.utcOffset(), 'minute').format('MMM D, YYYY'),
        size: getFileSizeStringForKb(properties.fileSizeKb),
        icon: getIconForFileViewItem(false, providerId),
        isNew: isNew,
        fileWarnings: properties.ingestionWarnings,
        docSource: getDocSourceFriendlyName(providerId)
    }
}

export function directoryToViewItem(directory: DgDirectory, providerId?: ProviderId): IDocumentViewItem {
    const props = directory.properties;
    return {
        id: props.path,
        name: props.name,
        folderId: '',
        docSet: '',
        createdAt: '',
        size: '',
        icon: getIconForFileViewItem(true, providerId),
        docSource: getDocSourceFriendlyName(providerId)
    }
}

// Default to 'Local Docs' as the doc source if not from an external provider
const getDocSourceFriendlyName = (providerId?: ProviderId) => providerId ? getProviderFriendlyName(providerId) : 'Local Docs';

export function getIconForFileViewItem(isFolder: boolean, providerId?: ProviderId): ColorIconProps {
    let iconName = isFolder ? dgIconNames.DgFolderIcon : dgIconNames.DgFileIcon;
    switch (providerId) {
        case ProviderId.SharePoint:
            iconName = isFolder ? dgIconNames.DgSharePointFolderIcon : dgIconNames.DgSharePointFileIcon;
            break;
        case ProviderId.OneDrive:
            iconName = isFolder ? dgIconNames.DgOneDriveFolderIcon : dgIconNames.DgOneDriveFileIcon;
            break;
        case ProviderId.Box:
            iconName = isFolder ? dgIconNames.DgBoxFolderIcon : dgIconNames.DgBoxFileIcon;
            break;
        case ProviderId.Dropbox:
            iconName = isFolder ? dgIconNames.DgDropboxFolderIcon : dgIconNames.DgDropboxFileIcon;
            break;
    }

    return {
        name: iconName,
        width: isFolder ? 27 : 18,
        height: 20,
        color: emptyGray
    }
}

export function getSortableDocumentFieldFromViewItemField(field: string): SortableDocumentField | undefined {
    if (field === 'name') {
        return SortableDocumentField.Filename;
    }
}